import * as THREE from 'three';

export class VideoPainting {
  constructor(scene, paintingData, lighting, audioListener) {
    this.scene = scene;
    this.data = paintingData;
    this.lighting = lighting;
    this.audioListener = audioListener;
    this.group = new THREE.Group();

    this.video = null;
    this.videoTexture = null;
    this.painting = null;
    this.frame = null;
    this.textLabel = null;
    this.spotlight = null;
    this.positionalAudio = null;

    this.isPlaying = false;
    this.audioEnabled = false;
    this.proximityRadius = 8; // Distance in units to start playing
    this.isVideoLoaded = false; // Track if video has been loaded
    this.isVideoLoading = false; // Track if video is currently loading

    this.createVideo();
    this.createFrame();
    this.createPainting();
    this.createTextLabel();
    this.createLighting();
    this.createAudio();

    // Position the entire group
    this.group.position.set(
      this.data.position.x,
      this.data.position.y,
      this.data.position.z
    );
    this.group.rotation.set(
      this.data.rotation.x,
      this.data.rotation.y,
      this.data.rotation.z
    );

    this.scene.add(this.group);
  }

  createVideo() {
    this.video = document.createElement('video');
    // Don't set src yet - will be loaded dynamically
    this.video.loop = true;
    this.video.muted = true; // Start muted for autoplay policy
    this.video.playsInline = true;
    this.video.crossOrigin = 'anonymous';
    this.video.preload = 'auto'; // Preload video data when src is set

    // Create video texture (will update when video loads)
    this.videoTexture = new THREE.VideoTexture(this.video);
    this.videoTexture.minFilter = THREE.LinearFilter;
    this.videoTexture.magFilter = THREE.LinearFilter;
  }

  /**
   * Load the video source dynamically
   * Returns a promise that resolves when video is loaded
   */
  loadVideo() {
    if (this.isVideoLoaded || this.isVideoLoading) {
      return Promise.resolve();
    }

    this.isVideoLoading = true;

    return new Promise((resolve) => {
      const onLoaded = () => {
        this.isVideoLoaded = true;
        this.isVideoLoading = false;
        console.log('Video loaded:', this.data.id);
        this.video.removeEventListener('loadeddata', onLoaded);
        this.video.removeEventListener('error', onError);
        resolve();
      };

      const onError = (e) => {
        console.error('Video error:', this.data.id, e);
        this.isVideoLoading = false;
        this.video.removeEventListener('loadeddata', onLoaded);
        this.video.removeEventListener('error', onError);
        resolve(); // Resolve anyway to not block loading
      };

      this.video.addEventListener('loadeddata', onLoaded);
      this.video.addEventListener('error', onError);

      // Set timeout for slow loads
      setTimeout(() => {
        if (this.isVideoLoading) {
          console.warn('Video loading timeout:', this.data.id);
          this.isVideoLoading = false;
          this.video.removeEventListener('loadeddata', onLoaded);
          this.video.removeEventListener('error', onError);
          resolve();
        }
      }, 15000); // 15 second timeout

      // Actually load the video
      this.video.src = this.data.videoUrl;
      this.video.load();
    });
  }

  /**
   * Unload the video to free memory
   */
  unloadVideo() {
    if (!this.isVideoLoaded && !this.isVideoLoading) {
      return;
    }

    // Pause and clear the video
    if (this.video) {
      this.video.pause();
      this.video.src = '';
      this.video.load(); // Reset the video element
    }

    // Disconnect audio if enabled
    if (this.positionalAudio && this.audioEnabled) {
      this.positionalAudio.disconnect();
      this.audioEnabled = false;
    }

    this.isVideoLoaded = false;
    this.isVideoLoading = false;
    this.isPlaying = false;
    console.log('Video unloaded:', this.data.id);
  }

  createFrame() {
    const frameThickness = 0.1;
    const frameDepth = 0.15;
    const width = this.data.scale.width;
    const height = this.data.scale.height;

    // Frame material based on style
    let frameMaterial;
    switch (this.data.frameStyle) {
      case 'gold':
        frameMaterial = new THREE.MeshStandardMaterial({
          color: 0xd4af37,
          roughness: 0.3,
          metalness: 0.8,
        });
        break;
      case 'dark-wood':
        frameMaterial = new THREE.MeshStandardMaterial({
          color: 0x2d1f1a,
          roughness: 0.8,
          metalness: 0.1,
        });
        break;
      case 'metal':
        frameMaterial = new THREE.MeshStandardMaterial({
          color: 0x888888,
          roughness: 0.2,
          metalness: 0.9,
        });
        break;
      default:
        frameMaterial = new THREE.MeshStandardMaterial({
          color: 0x1a1a1a,
          roughness: 0.5,
          metalness: 0.5,
        });
    }

    // Create frame as 4 bars
    const frameGroup = new THREE.Group();

    // Top
    const topFrame = new THREE.Mesh(
      new THREE.BoxGeometry(width + frameThickness * 2, frameThickness, frameDepth),
      frameMaterial
    );
    topFrame.position.set(0, height / 2 + frameThickness / 2, -frameDepth / 2);
    frameGroup.add(topFrame);

    // Bottom
    const bottomFrame = new THREE.Mesh(
      new THREE.BoxGeometry(width + frameThickness * 2, frameThickness, frameDepth),
      frameMaterial
    );
    bottomFrame.position.set(0, -height / 2 - frameThickness / 2, -frameDepth / 2);
    frameGroup.add(bottomFrame);

    // Left
    const leftFrame = new THREE.Mesh(
      new THREE.BoxGeometry(frameThickness, height, frameDepth),
      frameMaterial
    );
    leftFrame.position.set(-width / 2 - frameThickness / 2, 0, -frameDepth / 2);
    frameGroup.add(leftFrame);

    // Right
    const rightFrame = new THREE.Mesh(
      new THREE.BoxGeometry(frameThickness, height, frameDepth),
      frameMaterial
    );
    rightFrame.position.set(width / 2 + frameThickness / 2, 0, -frameDepth / 2);
    frameGroup.add(rightFrame);

    this.frame = frameGroup;
    this.group.add(frameGroup);
  }

  createPainting() {
    const geometry = new THREE.PlaneGeometry(this.data.scale.width, this.data.scale.height);
    const material = new THREE.MeshStandardMaterial({
      map: this.videoTexture,
      emissive: 0xffffff,
      emissiveMap: this.videoTexture,
      emissiveIntensity: 0.2, // Increased to compensate for no spotlight
      side: THREE.FrontSide,
      depthWrite: true,
      depthTest: true,
    });

    this.painting = new THREE.Mesh(geometry, material);
    this.painting.castShadow = false;
    this.painting.receiveShadow = false;

    // Use polygon offset to prevent z-fighting
    this.painting.renderOrder = 1;

    this.group.add(this.painting);
  }

  createTextLabel() {
    // Create canvas for text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 1024;
    canvas.height = 512;

    // Draw background panel
    context.fillStyle = 'rgba(0, 0, 0, 0.85)';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw border
    context.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    context.lineWidth = 4;
    context.strokeRect(0, 0, canvas.width, canvas.height);

    // Draw title
    context.fillStyle = '#ffffff';
    context.font = 'bold 56px Arial, sans-serif';
    context.textAlign = 'left';
    const titleY = 80;
    const padding = 40;
    context.fillText(this.data.title, padding, titleY);

    // Draw description with word wrapping
    context.fillStyle = '#aaaaaa';
    context.font = '36px Arial, sans-serif';
    const maxWidth = canvas.width - padding * 2;
    const lineHeight = 50;
    let descriptionY = titleY + 60;

    // Word wrap the description
    const words = this.data.description.split(' ');
    let line = '';

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = context.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth && i > 0) {
        context.fillText(line, padding, descriptionY);
        line = words[i] + ' ';
        descriptionY += lineHeight;
      } else {
        line = testLine;
      }
    }
    context.fillText(line, padding, descriptionY);

    // Create texture from canvas
    const textTexture = new THREE.CanvasTexture(canvas);
    textTexture.minFilter = THREE.LinearFilter;

    // Create text plane - positioned to the right of the painting
    const panelWidth = this.data.scale.width * 0.8;
    const panelHeight = panelWidth * 0.5; // 2:1 aspect ratio
    const textGeometry = new THREE.PlaneGeometry(panelWidth, panelHeight);
    const textMaterial = new THREE.MeshBasicMaterial({
      map: textTexture,
      transparent: true,
      opacity: 1.0,
      side: THREE.DoubleSide
    });

    this.textLabel = new THREE.Mesh(textGeometry, textMaterial);

    // Position to the right of the painting
    const xOffset = this.data.scale.width / 2 + panelWidth / 2 + 0.3; // Right side + half panel width + small gap
    this.textLabel.position.set(xOffset, 0, 0);

    this.group.add(this.textLabel);
  }

  createLighting() {
    // Paintings use emissive materials and don't need individual spotlights
    // This prevents hitting WebGL uniform limits with many paintings
    this.spotlight = null;
  }

  createAudio() {
    if (!this.audioListener) return;

    // Create positional audio
    this.positionalAudio = new THREE.PositionalAudio(this.audioListener);

    // Set reference distance for audio falloff
    this.positionalAudio.setRefDistance(3);
    this.positionalAudio.setMaxDistance(15);
    this.positionalAudio.setRolloffFactor(2);
    this.positionalAudio.setVolume(0.5);

    // Add audio to the painting group
    this.group.add(this.positionalAudio);
  }

  update(camera) {
    // Don't try to update if video isn't loaded yet
    if (!this.isVideoLoaded) {
      return;
    }

    const cameraPosition = camera.position;
    const paintingPosition = new THREE.Vector3(
      this.data.position.x,
      this.data.position.y,
      this.data.position.z
    );

    // Calculate distance from camera to painting
    const distance = cameraPosition.distanceTo(paintingPosition);

    // Sync isPlaying with actual video state (browser might pause it)
    if (this.video && !this.video.paused && !this.isPlaying) {
      this.isPlaying = true;
    } else if (this.video && this.video.paused && this.isPlaying) {
      this.isPlaying = false;
    }

    // Check if painting is visible (facing the camera)
    const isVisible = this.isPaintingVisible(camera, paintingPosition);

    // Play/pause video based on proximity AND visibility
    if (distance < this.proximityRadius && isVisible) {
      if (!this.isPlaying && this.video && this.video.paused) {
        this.play();
      }
    } else {
      if (this.isPlaying && this.video && !this.video.paused) {
        this.pause();
      }
    }
  }

  isPaintingVisible(camera, paintingPosition) {
    // Get direction from camera to painting
    const directionToPainting = new THREE.Vector3();
    directionToPainting.subVectors(paintingPosition, camera.position).normalize();

    // Get camera's forward direction
    const cameraDirection = new THREE.Vector3();
    camera.getWorldDirection(cameraDirection);

    // Calculate dot product (1 = directly in front, -1 = directly behind)
    const dotProduct = cameraDirection.dot(directionToPainting);

    // Check if painting is in front of camera (within 120-degree field of view)
    // cos(120°) = -0.5, so anything > -0.5 is within 240° total view
    // Using 0.3 means roughly 72° half-angle (144° total FOV)
    const isInFrontOfCamera = dotProduct > -0.3;

    if (!isInFrontOfCamera) {
      return false;
    }

    // Get painting's normal direction (what direction it's facing)
    const paintingNormal = new THREE.Vector3(0, 0, 1);
    paintingNormal.applyEuler(new THREE.Euler(
      this.data.rotation.x,
      this.data.rotation.y,
      this.data.rotation.z
    ));

    // Calculate direction from painting to camera
    const directionToCamera = new THREE.Vector3();
    directionToCamera.subVectors(camera.position, paintingPosition).normalize();

    // Check if camera is in front of painting surface
    // (not looking at the back of the painting)
    const paintingDot = paintingNormal.dot(directionToCamera);
    const isFacingCamera = paintingDot > 0.1; // Camera is in front of painting surface

    return isInFrontOfCamera && isFacingCamera;
  }

  play() {
    if (this.video && !this.isPlaying) {
      const playPromise = this.video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Playback started successfully
            this.isPlaying = true;
            console.log('Video playing:', this.data.id);

            // Connect audio after first successful play
            if (this.positionalAudio && !this.audioEnabled) {
              this.enableAudio();
            }
          })
          .catch(err => {
            // Video not ready yet or autoplay blocked - will retry on next update
            console.log('Video play pending for', this.data.id, ':', err.message);
            this.isPlaying = false;
          });
      }
    }
  }

  enableAudio() {
    if (this.positionalAudio && this.video && !this.audioEnabled) {
      try {
        // Resume audio context if needed (required for user interaction)
        if (this.audioListener.context.state === 'suspended') {
          this.audioListener.context.resume();
        }

        // Unmute video for spatial audio
        this.video.muted = false;

        this.positionalAudio.setMediaElementSource(this.video);
        this.audioEnabled = true;
        console.log('Audio enabled for painting:', this.data.id);
      } catch (err) {
        console.warn('Failed to enable audio for painting:', this.data.id, err);
      }
    }
  }

  pause() {
    if (this.video && this.isPlaying) {
      this.video.pause();
      this.isPlaying = false;
      console.log('Video paused:', this.data.id);
    }
  }

  getPaintingInfo() {
    return {
      title: this.data.title,
      description: this.data.description,
    };
  }

  dispose() {
    if (this.positionalAudio) {
      this.positionalAudio.disconnect();
      this.positionalAudio = null;
    }

    if (this.video) {
      this.video.pause();
      this.video.src = '';
      this.video = null;
    }

    if (this.videoTexture) {
      this.videoTexture.dispose();
    }

    if (this.painting) {
      this.painting.geometry.dispose();
      this.painting.material.dispose();
    }

    if (this.textLabel) {
      this.textLabel.geometry.dispose();
      this.textLabel.material.map.dispose();
      this.textLabel.material.dispose();
    }

    if (this.frame) {
      this.frame.children.forEach(child => {
        child.geometry.dispose();
        child.material.dispose();
      });
    }

    this.scene.remove(this.group);
  }
}
